/**
 * Helper pour gérer les tentatives de connexion échouées
 * Limite à 5 tentatives et bloque pendant 5 minutes après 5 échecs
 */

const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 5 * 60 * 1000; // 5 minutes en millisecondes
const STORAGE_KEY_PREFIX = 'loginAttempts_';

interface LoginAttemptsData {
	email: string;
	attempts: number;
	lastAttemptTime: number;
	lockedUntil?: number;
}

/**
 * Obtient la clé de stockage pour un email donné
 */
const getStorageKey = (email: string): string => {
	return `${STORAGE_KEY_PREFIX}${email.toLowerCase().trim()}`;
};

/**
 * Obtient les données de tentatives pour un email
 */
export const getLoginAttempts = (email: string): LoginAttemptsData | null => {
	try {
		const key = getStorageKey(email);
		const data = localStorage.getItem(key);
		if (!data) return null;
		return JSON.parse(data) as LoginAttemptsData;
	} catch (error) {
		console.error('Error reading login attempts:', error);
		return null;
	}
};

/**
 * Enregistre une tentative échouée
 */
export const recordFailedAttempt = (email: string): void => {
	try {
		const key = getStorageKey(email);
		const existing = getLoginAttempts(email);
		const now = Date.now();
		
		let attempts = 1;
		let lockedUntil: number | undefined;

		if (existing) {
			// Si le délai de blocage est expiré, réinitialiser
			if (existing.lockedUntil && existing.lockedUntil > now) {
				// Le compte est toujours bloqué, ne pas incrémenter
				return;
			}
			// Si le délai est expiré, réinitialiser le compteur
			if (existing.lockedUntil && existing.lockedUntil <= now) {
				attempts = 1;
			} else {
				attempts = existing.attempts + 1;
			}
		}

		// Si on atteint le maximum, bloquer le compte
		if (attempts >= MAX_ATTEMPTS) {
			lockedUntil = now + LOCKOUT_DURATION_MS;
		}

		const data: LoginAttemptsData = {
			email: email.toLowerCase().trim(),
			attempts,
			lastAttemptTime: now,
			lockedUntil,
		};

		localStorage.setItem(key, JSON.stringify(data));
	} catch (error) {
		console.error('Error recording failed attempt:', error);
	}
};

/**
 * Réinitialise les tentatives après une connexion réussie
 */
export const resetLoginAttempts = (email: string): void => {
	try {
		const key = getStorageKey(email);
		localStorage.removeItem(key);
	} catch (error) {
		console.error('Error resetting login attempts:', error);
	}
};

/**
 * Vérifie si le compte est actuellement bloqué
 */
export const isAccountLocked = (email: string): boolean => {
	const data = getLoginAttempts(email);
	if (!data || !data.lockedUntil) return false;

	const now = Date.now();
	if (data.lockedUntil > now) {
		return true;
	}

	// Si le délai est expiré, nettoyer les données
	if (data.lockedUntil <= now) {
		resetLoginAttempts(email);
		return false;
	}

	return false;
};

/**
 * Obtient le temps restant avant le déblocage (en millisecondes)
 */
export const getRemainingLockoutTime = (email: string): number => {
	const data = getLoginAttempts(email);
	if (!data || !data.lockedUntil) return 0;

	const now = Date.now();
	const remaining = data.lockedUntil - now;
	return remaining > 0 ? remaining : 0;
};

/**
 * Obtient le nombre de tentatives restantes avant blocage
 */
export const getRemainingAttempts = (email: string): number => {
	const data = getLoginAttempts(email);
	if (!data) return MAX_ATTEMPTS;
	return Math.max(0, MAX_ATTEMPTS - data.attempts);
};

